import { ReactNode } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from 'shared/ui/card'
import { User } from 'lucide-react'
import type { TemplateField, TemplateSection } from 'shared/api/templateService'
import type { PermitWorkflowTimeline } from 'modules/permit/api/permitService'
import { cn } from 'shared/ui/utils'
import { buildTemplateFieldValidationKey } from 'modules/permit/pages/permit-request/utils/validationTargets'

interface WorkflowSectionsSectionProps {
  sections: TemplateSection[]
  isReadOnly?: boolean
  canEditSection: (section: TemplateSection) => boolean
  renderTemplateFieldInput: (field: TemplateField, options?: { disabled?: boolean }) => ReactNode
  workflowTimeline?: PermitWorkflowTimeline | null
  getValidationContainerClass?: (key?: string | null) => string
}

/**
 * WorkflowSectionsSection encapsulates the repeating workflow cards so we can
 * reason about approvals/fields independently from the sprawling main form.
 */
export function WorkflowSectionsSection({
  sections,
  isReadOnly,
  canEditSection,
  renderTemplateFieldInput,
  workflowTimeline,
  getValidationContainerClass,
}: WorkflowSectionsSectionProps) {
  if (sections.length === 0) {
    return null
  }

  const formatDateTime = (value?: string | null): string => {
    if (!value) {
      return '—'
    }
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) {
      return value
    }
    return parsed.toLocaleString('th-TH', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  }

  const resolveApprovalSummary = (section: TemplateSection) => {
    if (!workflowTimeline || !section.approvalStageId) {
      return null
    }
    const stageIdKey = section.approvalStageId.trim().toLowerCase()
    const sectionCode = section.code?.trim().toUpperCase()
    const stage =
      workflowTimeline.stages.find(
        (item) => item.stageId?.trim().toLowerCase() === stageIdKey,
      ) ||
      (sectionCode
        ? workflowTimeline.stages.find(
            (item) => item.code?.trim().toUpperCase() === sectionCode,
          )
        : undefined)

    const historyItems = workflowTimeline.history.filter(
      (item) => item.stageId?.trim().toLowerCase() === stageIdKey,
    )
    const approvedHistory = historyItems
      .filter((item) => (item.statusAfter ?? '').toUpperCase() === 'APPROVED')
      .sort((a, b) => {
        const timeA = new Date(a.actedAt).getTime()
        const timeB = new Date(b.actedAt).getTime()
        if (Number.isNaN(timeA) || Number.isNaN(timeB)) {
          return (b.actedAt ?? '').localeCompare(a.actedAt ?? '')
        }
        return timeB - timeA
      })[0]

    const approver =
      approvedHistory?.actedByUserName ||
      approvedHistory?.actedByRoleName ||
      stage?.assignedRoleName ||
      stage?.assignedRoleCode ||
      'ไม่ระบุ'

    const approvedAt = approvedHistory?.actedAt
      ? formatDateTime(approvedHistory.actedAt)
      : stage?.status?.toUpperCase() === 'APPROVED' && stage.completedAt
        ? formatDateTime(stage.completedAt)
        : 'ยังไม่อนุมัติ'

    return {
      approver,
      approvedAt,
      stage,
    }
  }

  const resolveColumnSpanClass = (field?: TemplateField | null): string => {
    const layout = typeof field?.layout === 'number' ? field.layout : null
    return layout === 1 ? 'md:col-span-2' : 'md:col-span-1'
  }

  return (
    <>
      {sections.map((section) => {
        const sectionEditable = canEditSection(section)
        const approvalSummary = resolveApprovalSummary(section)
        const approvalDisplay = approvalSummary ?? {
          approver: 'ไม่ระบุ',
          approvedAt: 'ยังไม่อนุมัติ',
          stage: undefined,
        }
        const cardHighlightClass = sectionEditable
          ? 'border-emerald-300 shadow-emerald-100'
          : 'border-indigo-100'
        return (
          <Card
            key={section.templateSectionId ?? section.code}
            className={`border bg-white shadow-sm ${cardHighlightClass}`}
          >
            <CardHeader className="pb-4">
              <CardTitle
                className={`flex items-center gap-2 text-lg ${sectionEditable ? 'text-emerald-900' : 'text-indigo-900'}`}
              >
                <User className={`h-5 w-5 ${sectionEditable ? 'text-emerald-500' : 'text-indigo-500'}`} />
                {section.title || section.code || 'Workflow Section'}
              </CardTitle>
              <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                {section.approvalStageId ? (
                  <>
                    <span className="text-slate-600">
                      อนุมัติโดย:{' '}
                      <span className="font-medium text-slate-800">
                        {approvalDisplay.approver}
                      </span>
                    </span>
                    <span className="text-slate-400">•</span>
                    <span className="text-slate-600">
                      วันที่อนุมัติ:{' '}
                      <span className="font-medium text-slate-800">
                        {approvalDisplay.approvedAt}
                      </span>
                    </span>
                  </>
                ) : (
                  <span>ยังไม่ผูกกับขั้นตอนอนุมัติ</span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {section.fields && section.fields.length > 0 ? (
                <fieldset disabled={!sectionEditable} className="space-y-4 border-0 p-0">
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    {section.fields.map((field) => {
                      const node = renderTemplateFieldInput(field, { disabled: !sectionEditable })
                      if (!node) {
                        return null
                      }
                      const validationKey = buildTemplateFieldValidationKey(field)
                      return (
                        <div
                          key={field.fieldId ?? field.code}
                          className={cn(resolveColumnSpanClass(field), getValidationContainerClass?.(validationKey))}
                          data-validation-key={validationKey ?? undefined}
                        >
                          {node}
                        </div>
                      )
                    })}
                  </div>
                </fieldset>
              ) : (
                <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                  ยังไม่มีฟิลด์ใน section นี้
                </div>
              )}
            </CardContent>
          </Card>
        )
      })}
    </>
  )
}
